import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { IonIcon } from '@ionic/react';
import { timeOutline } from 'ionicons/icons';
import { format, parse } from 'date-fns';
import { FormEvent } from 'primereact/ts-helpers';
import { useMask } from '@react-input/mask';
import { Popover } from 'react-tiny-popover';
import Input from '../../../../Input/Input';
import Button from '../../../../Button/Button';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { TimeInputControl } from '../../../../../types/types';
import { useMinMaxDateTimeValue } from '../../hooks/useMinMaxDateTimeValue';
import { parseStrictTimeValue } from '../../utils/parseStrictTimeValue';
import { normalizeTimeSeconds } from '../../utils/normalizeTimeSeconds';
import { isTargetInsidePickerElements } from '../../utils/isTargetInsidePickerElements';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';
import type { GridInputBaseProps } from '../../types';
import {
  TIME_MASK_OPTIONS,
  TIME_INPUT_FORMAT,
  TIME_API_FORMAT,
  TIME_FILTER_DATE_VALUES,
  TIME_MASK,
} from '../../../../../constants';

type TimePickerProps = GridInputBaseProps & {
  control: TimeInputControl;
};

const TimePicker = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  isColumnFilter,
  control,
}: TimePickerProps) => {
  const { t } = useTranslate();
  const [time, setTime] = useState(new Date());
  const [displayedTime, setDisplayedTime] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const maskRef = useMask(TIME_MASK_OPTIONS);
  // Snapshot of the value at popover-open time so onSubmit can decide whether
  // the user actually changed anything (fire the handler only on a real diff).
  const popoverOpenValueRef = useRef<string | null>(null);

  // Emit value to both change and touch in the same moment — non-text controls
  // treat `touch` as an undebounced equivalent of `change`. `propagateHandler`
  // defaults to true; pass false for programmatic writes (mount defaults etc.)
  // to skip firing the field-level handlers in FormHandlersContext.
  const emitChange = (v: string, propagateHandler: boolean = true) => {
    onChange(v, propagateHandler);
    if (propagateHandler) {
      onTouch?.(v);
    }
  };

  // True when target is inside input or popover — onFocusIn/Out should not fire.
  const disableCallFocusHandler = (target: EventTarget | null): boolean =>
    isTargetInsidePickerElements(target, maskRef.current, popoverRef.current);

  const handleFocusIn = (e) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusIn?.();
    }
  };

  const handleFocusOut = (e) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusOut?.();
    }
  };

  const { minValue, maxValue } = useMinMaxDateTimeValue(
    control.min,
    control.max,
    true
  );

  useEffect(() => {
    let newTime = new Date();
    let hasCorrectValue = false;

    if (value) {
      const parsed = parseStrictTimeValue(normalizeTimeSeconds(value));
      if (parsed) {
        hasCorrectValue = true;
        newTime = parsed;
      } else {
        // Invalid or non-canonical time — ignore: show & submit empty.
        emitChange('', false);
        setError(null);
      }
    }

    if (!hasCorrectValue) {
      if (minValue && newTime < minValue) {
        newTime = new Date(minValue.getTime());
      } else if (maxValue && newTime > maxValue) {
        newTime = new Date(maxValue.getTime());
      }
    }

    const shouldColumnFilterPrefill = isColumnFilter && !value;
    if (hasCorrectValue || shouldColumnFilterPrefill) {
      checkMinMaxError(newTime, minValue, maxValue);
      const readableTimeString = format(newTime, TIME_INPUT_FORMAT);
      setDisplayedTime(readableTimeString);
      if (isColumnFilter || hasCorrectValue) {
        const timeString = format(newTime, TIME_API_FORMAT);
        emitChange(timeString, false);
      }
    } else {
      setDisplayedTime('');
    }
    newTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    setTime(newTime);
    //TODO: t in dependencies is a temporary fix for error messages not showing
    // It will probably not be needed after client-side routing merge
  }, [t]);

  const manualTimeChange = (v) => {
    if (v === TIME_MASK) {
      v = '';
    }
    setDisplayedTime(v);
    setError(null);
    if (v === '') {
      emitChange('');
    } else {
      const newTime = parse(v, TIME_INPUT_FORMAT, new Date());
      if (newTime.getDate()) {
        newTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        setTime(newTime);
        const timeString = format(newTime, TIME_API_FORMAT);
        emitChange(timeString);
      }
    }
  };

  const setInputAndApiValue = (
    newTime: Date,
    propagateHandler: boolean = true
  ) => {
    setError(null);
    const apiString = format(newTime, TIME_API_FORMAT);
    emitChange(apiString, propagateHandler);
    const inputString = format(newTime, TIME_INPUT_FORMAT);
    setDisplayedTime(inputString);
  };

  const checkMinMaxError = (time: Date, min: Date | null, max: Date | null) => {
    if (min && time < min) {
      const readableMinValue = format(min, TIME_INPUT_FORMAT);
      setError([t('errors.minValue').replace('{0}', readableMinValue)]);
      return true;
    } else if (max && time > max) {
      const readableMaxValue = format(max, TIME_INPUT_FORMAT);
      setError([t('errors.maxValue').replace('{0}', readableMaxValue)]);
      return true;
    }
    return false;
  };

  const onTimeChange = (e: FormEvent) => {
    const newTime = e.value as Date;
    newTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    setTime(newTime);
    // Keep formData in sync for live preview, but defer the field-level
    // handler until the user confirms via "use" (popover mode)
    setInputAndApiValue(newTime, false);
    if (isColumnFilter) {
      checkMinMaxError(newTime, minValue, maxValue);
    }
  };

  const onSubmit = () => {
    setError(null);
    if (time) {
      time.setFullYear(...TIME_FILTER_DATE_VALUES);
      // Fire the handler only when the user actually changed the value during
      // this popover session (formData was already kept in sync by onTimeChange).
      const apiString = format(time, TIME_API_FORMAT);
      const propagateHandler =
        apiString !== (popoverOpenValueRef.current ?? '');
      setInputAndApiValue(time, propagateHandler);
    }
    checkMinMaxError(time, minValue, maxValue);
    setPopoverOpen(false);
  };

  const onBlur = () => {
    if (displayedTime !== '') {
      const newTime = parse(displayedTime, TIME_INPUT_FORMAT, new Date());
      if (newTime.getDate()) {
        newTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        checkMinMaxError(newTime, minValue, maxValue);
      } else {
        setError([t('errors.invalidTime')]);
      }
    }
  };

  const onClickOutside = (e) => {
    handleFocusOut(e);
    setPopoverOpen(false);
    checkMinMaxError(time, minValue, maxValue);
  };

  // Enter commits the picked time, Escape closes without committing; both are swallowed so they don't bubble (e.g. into a parent column-filter Apply handler). Guarded on popoverOpen so the same keys do nothing while only the input is focused.
  const onPopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!popoverOpen) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setPopoverOpen(false);
    }
  };

  // Snapshot the current value every time the popover opens so onSubmit can
  // decide whether the user actually changed anything during this session.
  useEffect(() => {
    if (popoverOpen) {
      popoverOpenValueRef.current = value ? normalizeTimeSeconds(value) : '';
    }
  }, [popoverOpen]);

  const PickerElement = (
    <div className="timepicker-container">
      <Calendar value={time} onChange={onTimeChange} timeOnly inline />
    </div>
  );

  const InputElement = (
    <Input
      name={name}
      value={displayedTime}
      onChange={manualTimeChange}
      onKeyDown={onPopoverKeyDown}
      disabled={disabled}
      placeholder={t('timeInputPlaceholder')}
      inputRef={maskRef}
      error={error}
      onBlur={onBlur}
      onClick={isColumnFilter ? undefined : () => setPopoverOpen(true)}
      hideErrorText={!isColumnFilter}
    />
  );
  return isColumnFilter ? (
    <div
      className="time-filter-column-container"
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      {PickerElement}
      {InputElement}
    </div>
  ) : (
    <>
      <Popover
        isOpen={popoverOpen}
        positions={['bottom', 'top', 'left', 'right']}
        onClickOutside={onClickOutside}
        align="start"
        content={
          <div
            className="datetime-popover time-popover"
            ref={popoverRef}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          >
            {PickerElement}
            <div className="datetime-picker-footer">
              <Button onClick={onSubmit} fill="clear" color="secondary">
                {t('use')}
              </Button>
            </div>
          </div>
        }
      >
        <div
          className="datetime-picker-container"
          onFocus={handleFocusIn}
          onBlur={handleFocusOut}
        >
          <Button
            fill="clear"
            color="secondary"
            disabled={disabled}
            onClick={() => setPopoverOpen((v) => !v)}
          >
            <IonIcon icon={timeOutline} />
          </Button>
          {InputElement}
        </div>
      </Popover>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  );
};

export default TimePicker;
